import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router";
import FullScreenLoading from "@/components/FullScreenLoading";
import GenericErrorTemplate from "@/components/GenericErrorTemplate";
import { ROUTE_PARAMS } from "@/constants";
import { useAccount } from "@/context/account";
import { useQuery } from "@/hooks/apollo/useQuery";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";
import { useCustomerAccountIdsStore } from "@/store/useCustomerAccountIdsStore";
import { CustomerRouteContext } from "./CustomerRouteContext";
import { CUSTOMER_ROUTE_IDS } from "./customerRouteIdsQuery";

const replaceCustomerSegment = (
  pathname: string,
  routeSegment: string,
  iamId: string
): string => {
  const segments = pathname.split("/");
  const index = segments.findIndex(
    (segment, i) => segment === "customers" && segments[i + 1] === routeSegment
  );
  if (index === -1) {
    return pathname;
  }
  segments[index + 1] = iamId;
  return segments.join("/");
};

type CustomerRouteProviderProps = {
  children?: React.ReactNode;
};

/**
 * Resolves `:customerId` to the CDH id GraphQL expects. With IAM routing on, an
 * unknown segment is looked up in the dealer's customer list, and a legacy CDH
 * segment is rewritten to the IAM id.
 */
export const CustomerRouteProvider = (props: CustomerRouteProviderProps) => {
  const { children } = props;
  const { t } = useTranslation();
  const { account } = useAccount();
  const { pathname, search, hash } = useLocation();
  const { [ROUTE_PARAMS.CUSTOMER_ID]: routeSegment = "" } = useParams();
  const cdhIdByIamId = useCustomerAccountIdsStore(
    (state) => state.cdhIdByIamId
  );
  const iamIdByCdhId = useCustomerAccountIdsStore(
    (state) => state.iamIdByCdhId
  );
  const addCustomerAccountIds = useCustomerAccountIdsStore(
    (state) => state.addCustomerAccountIds
  );

  const migrationEnabled = isCdhIdIamIdMigrationEnabled();
  const storedCdhId = cdhIdByIamId.get(routeSegment);
  const storedIamId = iamIdByCdhId.get(routeSegment);
  const needsLookup =
    migrationEnabled &&
    routeSegment.length > 0 &&
    storedCdhId === undefined &&
    storedIamId === undefined;

  const { data, loading } = useQuery(CUSTOMER_ROUTE_IDS, {
    variables: { accountId: account.id },
    skip: !needsLookup,
    // A cached list can predate a customer that was just created or linked.
    fetchPolicy: "network-only",
    context: { skipGlobalError: true },
  });

  const listedCustomers = needsLookup ? data?.dealerCustomers : undefined;

  useEffect(() => {
    if (listedCustomers === undefined) {
      return;
    }
    addCustomerAccountIds(
      listedCustomers.flatMap(({ id, iamId }) =>
        id !== undefined && iamId !== undefined ? [{ cdhId: id, iamId }] : []
      )
    );
  }, [listedCustomers, addCustomerAccountIds]);

  const listedCustomer = listedCustomers?.find(
    (customer) =>
      customer.iamId === routeSegment || customer.id === routeSegment
  );

  let customerCdhId: string | undefined;
  let canonicalIamId: string | undefined;
  if (!migrationEnabled) {
    customerCdhId = routeSegment;
  } else if (storedCdhId !== undefined) {
    customerCdhId = storedCdhId;
  } else if (storedIamId !== undefined) {
    canonicalIamId = storedIamId;
  } else if (listedCustomer?.iamId === routeSegment) {
    customerCdhId = listedCustomer.id;
  } else if (listedCustomer !== undefined) {
    canonicalIamId = listedCustomer.iamId;
  }

  if (canonicalIamId !== undefined) {
    return (
      <Navigate
        to={`${replaceCustomerSegment(pathname, routeSegment, canonicalIamId)}${search}${hash}`}
        replace
      />
    );
  }

  if (customerCdhId === undefined) {
    if (loading) {
      return <FullScreenLoading />;
    }
    return (
      <GenericErrorTemplate
        header={t("customer_summary.customer_not_found")}
        description={t("customer_summary.customer_not_found_description")}
      />
    );
  }

  return (
    <CustomerRouteContext.Provider
      value={{ customerCdhId, customerRouteId: routeSegment }}
    >
      {children}
    </CustomerRouteContext.Provider>
  );
};
